import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Switch,
} from '@mui/material';
import PortalSnackbar from './Common/PortalSnackbar';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  Lightbulb as LightbulbIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  AccountBalance as AccountBalanceIcon,
  Category as CategoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { CurrencyGbp, ChartPie } from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../utils/api';
import { CURRENCY_SYMBOL } from '../utils/currency';
import useThemeStyles from '../theme/useThemeStyles';

const WEDDING_TYPES = [
  'luxury', 'traditional', 'destination', 'intimate', 'outdoor', 'diy'
];

const PAYMENT_METHODS = [
  'Cash',
  'Credit Card',
  'Debit Card',
  'Bank Transfer',
  'Cheque',
  'PayPal',
  'Other'
];

const POT_TYPES = [
  { value: 'couple', label: 'Couple' },
  { value: 'other', label: 'Other Contributor' }
];

function WeddingBudget({ embedded }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [_weddingDetails, setWeddingDetails] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [budgetData, setBudgetData] = useState({
    items: [],
    pots: [],
    categories: [],
    total: 0
  });

  // Vendor/business data
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [loadingMilestones, setLoadingMilestones] = useState(false);

  // New budget item form state
  const [newBudgetItem, setNewBudgetItem] = useState({
    category: '',
    description: '',
    estimatedCost: '',
    actualCost: '',
    amount: '',
    paid: false,
    paidDate: null,
    paymentDueDate: null,
    paymentMethod: '',
    milestoneId: '',
    potSourceId: '',
    potSources: [],
    notes: '',
    order: 0
  });
  
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Pots management
  const [showPotDialog, setShowPotDialog] = useState(false);
  const [editingPot, setEditingPot] = useState(null);
  const [potForm, setPotForm] = useState({
    name: '',
    amount: '',
    type: 'couple',
    contributorName: ''
  });

  // Categories management
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#85929E',
    order: 0,
    goalAmount: ''
  });

  // AI features
  const [suggestingCategory, setSuggestingCategory] = useState(false);
  const [categoryRecommendation, setCategoryRecommendation] = useState(null);
  const [_aiFeatureEnabled, _setAiFeatureEnabled] = useState(true);
  
  // Budget recommendation states
  const [showRecommendationDialog, setShowRecommendationDialog] = useState(false);
  const [guestCount, setGuestCount] = useState('');
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [budgetRecommendations, setBudgetRecommendations] = useState(null);
  const [appliedRecommendations, setAppliedRecommendations] = useState(new Set());
  const [_duplicateWarning, _setDuplicateWarning] = useState(null);
  const [spendingAnalysis, setSpendingAnalysis] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);

  // Filtering and sorting
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    pot: '',
    vendor: '',
    paidStatus: 'all',
    minAmount: '',
    maxAmount: '',
    dateFrom: null,
    dateTo: null
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'order',
    direction: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState(0);

  // Selected items for bulk operations
  const [selectedItems, setSelectedItems] = useState([]);

  // Templates
  const [templates, setTemplates] = useState({ systemTemplates: [], customTemplates: [] });
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    weddingType: 'custom'
  });

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  useEffect(() => {
    fetchData();
    fetchVendors();
    fetchTemplates();
    fetchMilestones();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const response = await api.get('/api/user-suppliers');
      
      // Transform to get business data and filter out null/undefined values
      const businesses = (response.data || [])
        .filter(us => us && us.supplier && us.supplier._id)
        .map(us => ({
          _id: us.supplier._id,
          name: us.supplier.businessName || us.supplier.name || '',
          category: us.supplier.category || '',
          email: us.supplier.email || '',
          phone: us.supplier.phone || ''
        }))
        .filter(vendor => vendor && vendor._id); // Final filter to ensure no null vendors
      
      setVendors(businesses);
    } catch (error) {
      setVendors([]); // Set to empty array on error
      // Don't show error - vendors are optional
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get('/api/wedding/budget/templates');
      setTemplates({
        systemTemplates: response.data.systemTemplates || [],
        customTemplates: response.data.customTemplates || []
      });
    } catch (error) {} finally {
      setLoadingTemplates(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      setLoadingMilestones(true);
      const response = await api.get('/api/wedding/budget/milestones');
      setMilestones(response.data || []);
    } catch (error) {} finally {
      setLoadingMilestones(false);
    }
  };

  const handleApplyTemplate = async (templateId, replaceExisting = false) => {
    try {
      setApplyingTemplate(true);
      setError('');
      
      const response = await api.post(`/api/wedding/budget/templates/${templateId}/apply`, {
        replaceExisting
      });

      if (response.data.success) {
        await fetchData();
        setShowTemplateDialog(false);
        setSuccess(replaceExisting ? 'Template applied successfully' : 'Template merged successfully');
      } else {
        setError(response.data.message || 'Failed to apply template');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (!templateForm.name.trim()) {
        setError('Template name is required');
        return;
      }

      const response = await api.post('/api/wedding/budget/templates/save-current', templateForm);

      if (response.data.success) {
        await fetchTemplates();
        setShowSaveTemplateDialog(false);
        setTemplateForm({ name: '', description: '', weddingType: 'custom' });
        setSuccess('Budget saved as template successfully');
      } else {
        setError(response.data.message || 'Failed to save template');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template?',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        try {
          setLoadingTemplates(true);
          const response = await api.delete(`/api/wedding/budget/templates/${templateId}`);

          if (response.data.success) {
            await fetchTemplates();
            setSuccess('Template deleted successfully');
          } else {
            setError(response.data.message || 'Failed to delete template');
          }
        } catch (error) {
          setError(error.response?.data?.message || 'Failed to delete template');
        } finally {
          setLoadingTemplates(false);
        }
      }
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [weddingRes, budgetRes] = await Promise.all([
        api.get('/api/wedding'),
        api.get('/api/wedding/budget')
      ]);
      
      setWedding(weddingRes.data);
      
      // Budget API returns { items, pots, categories, total }
      if (budgetRes.data) {
        const total = budgetRes.data.total || 0;
        setBudgetData({
          items: budgetRes.data.items || [],
          pots: budgetRes.data.pots || [],
          categories: budgetRes.data.categories || [],
          total: total
        });

        // Initialize pots if empty
        if (budgetRes.data.pots && budgetRes.data.pots.length === 0) {
          // Default pot will be created by backend, but ensure frontend has it
        }
      }
      
      // Also fetch wedding details for compatibility
      try {
        const detailsRes = await api.get('/api/wedding-details');
        if (detailsRes.data && detailsRes.data.budget) {
          setWeddingDetails(detailsRes.data);
        }
      } catch (err) {
        // Ignore if wedding details don't exist
      }
    } catch (error) {
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color) return false;
    // Remove # if present
    const hex = color.replace('#', '');
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate luminance (perceived brightness)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // If luminance is greater than 0.5, it's a light color
    return luminance > 0.5;
  };

  // Get available categories (default + custom)
  const getAvailableCategories = () => {
    if (!budgetData.categories || budgetData.categories.length === 0) {
      return ['venue', 'catering', 'photography', 'flowers', 'music', 'attire', 'decor', 'transport', 'other'];
    }
    return budgetData.categories.map(cat => cat.name);
  };

  const handleBudgetItemChange = (field) => (event) => {
    const value = field === 'paid' ? event.target.checked : 
                  (field === 'estimatedCost' || field === 'actualCost' || field === 'amount') ?
                  (event.target.value === '' ? '' : parseFloat(event.target.value)) :
                  event.target.value;

    setNewBudgetItem(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate amount from estimatedCost if actualCost not set
    if (field === 'estimatedCost' && !newBudgetItem.actualCost) {
      setNewBudgetItem(prev => ({
        ...prev,
        amount: value
      }));
    }

    // If description is being changed and has enough content, suggest a category
    // AI suggestive assistant disabled for now
    // if (field === 'description' && value.length > 3 && aiFeatureEnabled) {
    //   suggestCategory(value);
    // }
  };

  const _suggestCategory = async (description) => {
    if (suggestingCategory) return;
    
    try {
      setSuggestingCategory(true);
      
      const categories = getAvailableCategories();
      const response = await api.post('/api/ai/suggest-category', { 
        description,
        categories
      });
      
      if (response.data && response.data.category) {
        setCategoryRecommendation(response.data.category);
      }
    } catch (error) {} finally {
      setSuggestingCategory(false);
    }
  };

  const applyRecommendedCategory = () => {
    if (categoryRecommendation) {
      setNewBudgetItem(prev => ({
        ...prev,
        category: categoryRecommendation
      }));
      setCategoryRecommendation(null);
    }
  };

  const handleAddBudgetItem = async () => {
    if (!newBudgetItem.category || !newBudgetItem.description || !newBudgetItem.estimatedCost) {
      setError('Please fill in category, description, and estimated cost');
      return;
    }

    const isEditing = !!editingItem;
    try {
      setSaving(true);
      setError('');

      // Prepare pot sources - use new format if available, otherwise fall back to old format
      let potSources = null;
      if (newBudgetItem.potSources && Array.isArray(newBudgetItem.potSources) && newBudgetItem.potSources.length > 0) {
        // Filter out empty entries and validate
        potSources = newBudgetItem.potSources
          .filter(ps => ps && ps.potId && ps.amount !== undefined && ps.amount !== '')
          .map(ps => ({
            potId: ps.potId,
            amount: parseFloat(ps.amount) || 0
          }));
        // Only send if we have valid entries
        if (potSources.length === 0) {
          potSources = null;
        }
      }

      const itemData = {
        category: newBudgetItem.category,
        description: newBudgetItem.description,
        estimatedCost: parseFloat(newBudgetItem.estimatedCost),
        actualCost: newBudgetItem.actualCost ? parseFloat(newBudgetItem.actualCost) : undefined,
        amount: newBudgetItem.estimatedCost, // Use estimatedCost as default amount
        paid: newBudgetItem.paid,
        paidDate: newBudgetItem.paidDate ? format(newBudgetItem.paidDate, 'yyyy-MM-dd') : undefined,
        paymentDueDate: newBudgetItem.paymentDueDate ? format(newBudgetItem.paymentDueDate, 'yyyy-MM-dd') : undefined,
        paymentMethod: newBudgetItem.paymentMethod || null,
        milestoneId: newBudgetItem.milestoneId || null,
        potSources: potSources, // Send new format
        potSourceId: potSources ? null : (newBudgetItem.potSourceId || null), // Only send old format if new format not used
        vendorId: newBudgetItem.vendorId || null,
        notes: newBudgetItem.notes || undefined
      };

      if (isEditing) {
        // Update existing item
        await api.put(`/api/wedding/budget/items/${editingItem.id}`, itemData);
      } else {
        // Create new item
        await api.post('/api/wedding/budget', itemData);
      }

      // Reset form
      setNewBudgetItem({
        category: '',
        description: '',
        estimatedCost: '',
        actualCost: '',
        amount: '',
        paid: false,
        paidDate: null,
        paymentMethod: '',
        milestoneId: '',
        potSourceId: '',
        potSources: [],
        vendorId: '',
        notes: '',
        order: 0
      });
      setCategoryRecommendation(null);
      setShowItemForm(false);
      setEditingItem(null);

      // Refresh data to ensure UI is in sync
      await fetchData();
      setSuccess(isEditing ? 'Budget item updated successfully' : 'Budget item added successfully');
    } catch (error) {
      setError(error.response?.data?.message || (isEditing ? 'Failed to update budget item' : 'Failed to add budget item'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    // Handle both old format (potSourceId) and new format (potSources array)
    let potSources = [];
    if (item.potSources && Array.isArray(item.potSources) && item.potSources.length > 0) {
      // Use new format
      potSources = item.potSources.map(ps => ({ potId: ps.potId, amount: ps.amount || '' }));
    } else if (item.potSourceId) {
      // Convert old format to new format for editing
      potSources = [{ potId: item.potSourceId, amount: item.estimatedCost || item.amount || '' }];
    }
    
    setNewBudgetItem({
      category: item.category || '',
      description: item.description || '',
      estimatedCost: item.estimatedCost || item.amount || '',
      actualCost: item.actualCost || '',
      amount: item.amount || '',
      paid: item.paid || false,
      paidDate: item.paidDate ? new Date(item.paidDate) : null,
      paymentDueDate: item.paymentDueDate ? new Date(item.paymentDueDate) : null,
      paymentMethod: item.paymentMethod || '',
      milestoneId: item.milestoneId || '',
      potSourceId: item.potSourceId || '', // Keep for backward compatibility
      potSources: potSources,
      vendorId: item.vendorId || '',
      notes: item.notes || '',
      order: item.order || 0
    });
    setShowItemForm(true);
  };

  // Helper function to clean NaN values from items
  const cleanBudgetItems = (items) => {
    return items.map(item => {
      const cleaned = { ...item };
      
      // Clean amount - ensure it's a valid number
      if (cleaned.amount === undefined || cleaned.amount === null || (typeof cleaned.amount === 'number' && isNaN(cleaned.amount))) {
        cleaned.amount = cleaned.estimatedCost && !isNaN(cleaned.estimatedCost) ? cleaned.estimatedCost : 0;
      } else if (typeof cleaned.amount !== 'number') {
        const parsed = parseFloat(cleaned.amount);
        cleaned.amount = isNaN(parsed) ? 0 : parsed;
      }
      
      // Clean estimatedCost - ensure it's a valid number
      if (cleaned.estimatedCost === undefined || cleaned.estimatedCost === null || (typeof cleaned.estimatedCost === 'number' && isNaN(cleaned.estimatedCost))) {
        cleaned.estimatedCost = cleaned.amount && !isNaN(cleaned.amount) ? cleaned.amount : 0;
      } else if (typeof cleaned.estimatedCost !== 'number') {
        const parsed = parseFloat(cleaned.estimatedCost);
        cleaned.estimatedCost = isNaN(parsed) ? 0 : parsed;
      }
      
      // Clean actualCost - can be undefined if not set
      if (cleaned.actualCost !== undefined && cleaned.actualCost !== null) {
        if (typeof cleaned.actualCost === 'number' && isNaN(cleaned.actualCost)) {
          cleaned.actualCost = undefined;
        } else if (typeof cleaned.actualCost !== 'number') {
          const parsed = parseFloat(cleaned.actualCost);
          cleaned.actualCost = isNaN(parsed) ? undefined : parsed;
        }
      }
      
      // Clean order - ensure it's a valid number
      if (cleaned.order === undefined || cleaned.order === null || (typeof cleaned.order === 'number' && isNaN(cleaned.order))) {
        cleaned.order = 0;
      } else if (typeof cleaned.order !== 'number') {
        const parsed = parseInt(cleaned.order);
        cleaned.order = isNaN(parsed) ? 0 : parsed;
      }
      
      // Final validation - ensure all numeric fields are actual numbers, not NaN
      const amountNum = Number(cleaned.amount);
      cleaned.amount = (isNaN(amountNum) || amountNum === null || amountNum === undefined) ? 0 : amountNum;
      
      const estimatedCostNum = Number(cleaned.estimatedCost);
      cleaned.estimatedCost = (isNaN(estimatedCostNum) || estimatedCostNum === null || estimatedCostNum === undefined) 
        ? (cleaned.amount || 0) 
        : estimatedCostNum;
      
      if (cleaned.actualCost !== undefined && cleaned.actualCost !== null) {
        const actualCostNum = Number(cleaned.actualCost);
        cleaned.actualCost = (isNaN(actualCostNum) || actualCostNum === null) ? undefined : actualCostNum;
      }
      
      const orderNum = Number(cleaned.order);
      cleaned.order = (isNaN(orderNum) || orderNum === null || orderNum === undefined) ? 0 : orderNum;
      
      return cleaned;
    });
  };

  const handleRemoveBudgetItem = async (itemId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Budget Item',
      message: 'Are you sure you want to delete this budget item?',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        try {
          // Filter out the deleted item
          const updatedItems = budgetData.items.filter(item => item.id !== itemId);
          
          // Clean NaN values before sending
          const cleanedItems = cleanBudgetItems(updatedItems);
          
          // Update local state
          setBudgetData(prev => ({ ...prev, items: cleanedItems }));
          
          // Update via wedding-details endpoint
          const weddingDetailsData = {
            wedding: wedding._id,
            budget: {
              ...budgetData,
              items: cleanedItems
            }
          };
          
          await api.post('/api/wedding-details', weddingDetailsData);
          await fetchData();
          setSuccess('Budget item deleted successfully');
        } catch (error) {
          setError('Failed to delete budget item');
          await fetchData(); // Refresh on error
        }
      }
    });
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    setConfirmDialog({
      open: true,
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${selectedItems.length} selected item(s)?`,
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        try {
          setSaving(true);
          setError('');
          
          const itemsToDelete = [...selectedItems];
          const selectedItemIds = new Set(itemsToDelete);
          
          // Filter out all selected items at once
          const remainingItems = budgetData.items.filter(item => !selectedItemIds.has(item.id));
          
          // Clean NaN values before sending
          const cleanedItems = cleanBudgetItems(remainingItems);
          
          // Update backend with all remaining items in one operation
          const weddingDetailsData = {
            wedding: wedding._id,
            budget: {
              ...budgetData,
              items: cleanedItems
            }
          };
          
          await api.post('/api/wedding-details', weddingDetailsData);
          
          // Update local state
          setBudgetData(prev => ({ ...prev, items: cleanedItems }));
          
          // Refresh to ensure everything is in sync
          await fetchData();
          
          const deletedCount = itemsToDelete.length;
          setSelectedItems([]);
          setSuccess(`Successfully deleted ${deletedCount} item(s)`);
        } catch (error) {
          setError('Failed to delete items. Please try again.');
          await fetchData(); // Refresh on error to show actual state
        } finally {
          setSaving(false);
        }
      }
    });
  };

  // Select/deselect all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredItems.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Toggle item selection
  const handleToggleItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Pots management
  const handleAddPot = async () => {
    if (!potForm.name || !potForm.amount || !potForm.type) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const potData = {
        ...(editingPot && { id: editingPot.id }),
        name: potForm.name,
        amount: parseFloat(potForm.amount),
        type: potForm.type,
        contributorName: potForm.type === 'other' ? potForm.contributorName : undefined
      };

      const response = await api.post('/api/wedding/budget/pots', potData);

      if (response.data.success) {
        setBudgetData(prev => ({
          ...prev,
          pots: response.data.pots,
          total: response.data.total
        }));
        setShowPotDialog(false);
        setPotForm({ name: '', amount: '', type: 'couple', contributorName: '' });
        setEditingPot(null);
        setSuccess('Budget pot saved successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save pot');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePot = async (potId) => {
    // Check if pot has items linked to it (support both old and new formats)
    const potItems = budgetData.items.filter(item => {
      // Check old format (potSourceId)
      if (item.potSourceId === potId) return true;
      // Check new format (potSources array)
      if (item.potSources && Array.isArray(item.potSources)) {
        return item.potSources.some(ps => ps.potId === potId);
      }
      return false;
    });
    const hasItems = potItems.length > 0;
    
    setConfirmDialog({
      open: true,
      title: 'Delete Budget Pot',
      message: hasItems 
        ? `This pot has ${potItems.length} item(s) linked to it. Items will be unlinked from this pot when you delete it. Are you sure you want to continue?`
        : 'Are you sure you want to delete this budget pot?',
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        try {
          const response = await api.delete(`/api/wedding/budget/pots/${potId}`, {
            params: { unlinkItems: true }
          });

          if (response.data.success) {
            setBudgetData(prev => ({
              ...prev,
              pots: response.data.pots,
              total: response.data.total,
              items: response.data.items || prev.items.map(item => 
                item.potSourceId === potId ? { ...item, potSourceId: '' } : item
              )
            }));
            setSuccess(hasItems 
              ? `Budget pot deleted successfully. ${potItems.length} item(s) have been unlinked.`
              : 'Budget pot deleted successfully'
            );
            // Close the edit dialog if it's open
            if (editingPot && editingPot.id === potId) {
              setShowPotDialog(false);
              setEditingPot(null);
            }
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to delete pot';
          setError(errorMessage);
          // If backend doesn't support unlinkItems param, try without it
          if (errorMessage.includes('items') && hasItems) {
            try {
              const retryResponse = await api.delete(`/api/wedding/budget/pots/${potId}`);
              if (retryResponse.data.success) {
                setBudgetData(prev => ({
                  ...prev,
                  pots: retryResponse.data.pots,
                  total: retryResponse.data.total,
                  items: prev.items.map(item => 
                    item.potSourceId === potId ? { ...item, potSourceId: '' } : item
                  )
                }));
                setSuccess(hasItems 
                  ? `Budget pot deleted successfully. ${potItems.length} item(s) have been unlinked.`
                  : 'Budget pot deleted successfully'
                );
                if (editingPot && editingPot.id === potId) {
                  setShowPotDialog(false);
                  setEditingPot(null);
                }
              }
            } catch (retryError) {
              setError(retryError.response?.data?.message || 'Failed to delete pot. Please unlink items first.');
            }
          }
        }
      }
    });
  };

  // Categories management
  const handleAddCategory = async () => {
    if (!categoryForm.name) {
      setError('Category name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const categoryData = {
        name: categoryForm.name,
        color: categoryForm.color,
        order: categoryForm.order || 0,
        goalAmount: categoryForm.goalAmount ? parseFloat(categoryForm.goalAmount) : undefined
      };

      const response = await api.post('/api/wedding/budget/categories', categoryData);

      if (response.data.success) {
        setBudgetData(prev => ({
          ...prev,
          categories: response.data.categories
        }));
        setShowCategoryDialog(false);
        setCategoryForm({ name: '', color: '#85929E', order: 0, goalAmount: '' });
        setEditingCategory(null);
        setSuccess('Category saved successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Category',
      message: `Are you sure you want to delete the category "${categoryName}"?`,
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        try {
          const response = await api.delete(`/api/wedding/budget/categories/${encodeURIComponent(categoryName)}`);

          if (response.data.success) {
            setBudgetData(prev => ({
              ...prev,
              categories: response.data.categories
            }));
            setSuccess('Category deleted successfully');
          }
        } catch (error) {
          setError(error.response?.data?.message || 'Failed to delete category');
        }
      }
    });
  };

  // Filtering and sorting
  const getFilteredAndSortedItems = () => {
    let filtered = [...budgetData.items];

    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.description?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }

    if (filters.pot) {
      filtered = filtered.filter(item => {
        // Check old format (potSourceId)
        if (item.potSourceId === filters.pot) return true;
        // Check new format (potSources array)
        if (item.potSources && Array.isArray(item.potSources)) {
          return item.potSources.some(ps => ps.potId === filters.pot);
        }
        return false;
      });
    }

    if (filters.vendor) {
      filtered = filtered.filter(item => item.vendorId === filters.vendor);
    }

    if (filters.paidStatus !== 'all') {
      filtered = filtered.filter(item => 
        filters.paidStatus === 'paid' ? item.paid : !item.paid
      );
    }

    if (filters.minAmount) {
      const min = parseFloat(filters.minAmount);
      filtered = filtered.filter(item => (item.amount || 0) >= min);
    }

    if (filters.maxAmount) {
      const max = parseFloat(filters.maxAmount);
      filtered = filtered.filter(item => (item.amount || 0) <= max);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(item => {
        if (!item.paidDate) return false;
        const itemDate = new Date(item.paidDate);
        return itemDate >= filters.dateFrom;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(item => {
        if (!item.paidDate) return false;
        const itemDate = new Date(item.paidDate);
        return itemDate <= filters.dateTo;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'amount':
          aValue = parseFloat(a.amount || a.estimatedCost || 0);
          bValue = parseFloat(b.amount || b.estimatedCost || 0);
          break;
        case 'date':
          aValue = a.paidDate ? new Date(a.paidDate).getTime() : 0;
          bValue = b.paidDate ? new Date(b.paidDate).getTime() : 0;
          break;
        case 'pot':
          // Handle both old and new formats for sorting
          if (a.potSources && Array.isArray(a.potSources) && a.potSources.length > 0) {
            aValue = a.potSources.map(ps => ps.potId).join(', ');
          } else {
            aValue = a.potSourceId || '';
          }
          if (b.potSources && Array.isArray(b.potSources) && b.potSources.length > 0) {
            bValue = b.potSources.map(ps => ps.potId).join(', ');
          } else {
            bValue = b.potSourceId || '';
          }
          break;
        default:
          aValue = a.order || 0;
          bValue = b.order || 0;
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    });

    return filtered;
  };

  const calculateTotalSpent = () => {
    return budgetData.items.reduce((total, item) => {
      const cost = item.actualCost || item.estimatedCost || item.amount || 0;
      return total + parseFloat(cost);
    }, 0);
  };

  const calculateTotalBudgetFromPots = () => {
    return budgetData.pots.reduce((total, pot) => {
      return total + parseFloat(pot.amount || 0);
    }, 0);
  };

  const calculateRemainingBudget = () => {
    const totalBudget = calculateTotalBudgetFromPots();
    return totalBudget - calculateTotalSpent();
  };

  const handleGetRecommendations = async () => {
    try {
      setLoadingRecommendations(true);
      const totalBudget = calculateTotalBudgetFromPots();
      
      const response = await api.post('/api/ai/budget-recommendations', {
        totalBudget: totalBudget || 0,
        guestCount: guestCount ? parseInt(guestCount, 10) : null,
        customCategories: budgetData.categories.filter(c => c.isCustom).map(c => c.name)
      });
      
      setBudgetRecommendations(response.data);
      setAppliedRecommendations(new Set()); // Reset applied recommendations
    } catch (error) {
      setError('Failed to get recommendations');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const applySingleRecommendation = async (category, data) => {
    try {
      setSaving(true);
      setError('');
      
      const totalBudget = calculateTotalBudgetFromPots();
      const percentage = typeof data === 'object' && data.percentage ? data.percentage : data;
      const amount = totalBudget * (percentage / 100);
      
      const item = {
        category: category.charAt(0).toUpperCase() + category.slice(1),
        description: `${category.charAt(0).toUpperCase() + category.slice(1)} budget allocation`,
        estimatedCost: amount,
        amount: amount,
        paid: false,
        order: 0
      };
      
      const response = await api.post('/api/wedding/budget', item);
      
      // Mark as applied
      setAppliedRecommendations(prev => new Set([...prev, category]));
      
      // Update budget data without full refresh to avoid modal flash
      // Fetch budget items without setting loading state
      try {
        const budgetRes = await api.get('/api/wedding/budget');
        if (budgetRes.data) {
          setBudgetData(prev => ({
            ...prev,
            items: budgetRes.data.items || prev.items || []
          }));
        }
      } catch (err) {
        // If refresh fails, try to add item from response if available
        if (response.data && response.data.item) {
          setBudgetData(prev => ({
            ...prev,
            items: [...(prev.items || []), response.data.item]
          }));
        }
      }
      
      setSuccess(`Successfully added ${item.category} budget item!`);
    } catch (error) {
      setError(`Failed to add ${category} recommendation. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const applyRecommendations = async () => {
    if (!budgetRecommendations || !budgetRecommendations.recommendations) return;
    
    try {
      setSaving(true);
      setError('');
      
      const totalBudget = calculateTotalBudgetFromPots();
      const recommendations = budgetRecommendations.recommendations;
      
      // Create budget items from recommendations
      const newItems = Object.entries(recommendations).map(([category, data]) => {
        const percentage = typeof data === 'object' && data.percentage ? data.percentage : data;
        const amount = totalBudget * (percentage / 100);
        return {
          category: category.charAt(0).toUpperCase() + category.slice(1),
          description: `${category.charAt(0).toUpperCase() + category.slice(1)} budget allocation`,
          estimatedCost: amount,
          amount: amount,
          paid: false,
          order: 0
        };
      });
      
      // Add all items to budget sequentially to avoid version conflicts
      let successful = 0;
      let failed = 0;
      
      for (const item of newItems) {
        try {
          await api.post('/api/wedding/budget', item);
          successful++;
          // Small delay to ensure document version is updated
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          failed++;
          // Continue with next item even if one fails
        }
      }
      
      // Mark all as applied
      setAppliedRecommendations(new Set(Object.keys(recommendations)));
      
      // Refresh budget data
      await fetchData();
      
      // Show success/error messages
      if (failed === 0) {
        setSuccess(`Successfully added ${successful} budget items from recommendations!`);
      } else if (successful > 0) {
        setSuccess(`Added ${successful} items. ${failed} items failed to add.`);
      } else {
        setError('Failed to add recommendations. Please try again.');
      }
      
      setShowRecommendationDialog(false);
      setBudgetRecommendations(null);
    } catch (error) {
      setError('Failed to apply recommendations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Budget analytics
  const loadAnalytics = async () => {
    try {
      const response = await api.get('/api/wedding/budget/analytics');
      setSpendingAnalysis(response.data);
      setShowAnalysisDialog(true);
    } catch (error) {
      setError('Failed to load analytics');
    }
  };

  // Drag and drop handler
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Only allow reordering when sorted by order
    if (sortConfig.field !== 'order') {
      setError('Drag-and-drop reordering is only available when sorted by "Default Order"');
      return;
    }

    // If dropped in the same position, do nothing
    if (result.destination.index === result.source.index) return;

    // Get the currently displayed items (should be sorted by order)
    const displayedItems = getFilteredAndSortedItems();
    
    // Reorder the displayed items
    const reorderedDisplayedItems = Array.from(displayedItems);
    const [movedItem] = reorderedDisplayedItems.splice(result.source.index, 1);
    reorderedDisplayedItems.splice(result.destination.index, 0, movedItem);

    // Update order values based on new positions
    const updatedItems = reorderedDisplayedItems.map((item, index) => ({
      ...item,
      order: index
    }));

    // Create a map of item IDs to their new orders for efficient lookup
    const orderMap = new Map(updatedItems.map(item => [item.id, item.order]));

    // Update the full items array with new orders
    const updatedFullItems = budgetData.items.map(item => {
      const newOrder = orderMap.get(item.id);
      if (newOrder !== undefined) {
        return { ...item, order: newOrder };
      }
      return item;
    });

    // Sort the full items array by order to maintain consistency
    updatedFullItems.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Update local state immediately to avoid flash
    setBudgetData(prev => ({
      ...prev,
      items: updatedFullItems
    }));

    // Update order on server in the background
    try {
      const itemOrders = updatedItems.map((item) => ({
        itemId: item.id,
        order: item.order
      }));

      const response = await api.put('/api/wedding/budget/items/reorder', {
        itemOrders
      });

      if (response.data.success) {
        // Silently sync with server response to ensure consistency
        setBudgetData(prev => ({
          ...prev,
          items: response.data.items || prev.items
        }));
      }
    } catch (error) {
      setError('Failed to update item order');
      // Refresh to revert changes on error
      await fetchData();
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredItems = getFilteredAndSortedItems();
  const totalSpent = calculateTotalSpent();
  const totalBudgetFromPots = calculateTotalBudgetFromPots();
  const remainingBudget = calculateRemainingBudget();
  const availableCategories = getAvailableCategories();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={embedded ? { p: 0 } : styles.container.page}>
        <Box sx={{
          ...styles.patterns.pageHeader,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: embedded ? 1.5 : undefined,
        }}>
          <Box sx={{ flex: 1, px: embedded ? 2 : 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Budget
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track expenses and allocations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              onClick={loadAnalytics}
              startIcon={<ChartPie size={20} />}
              sx={{ alignSelf: { xs: 'stretch', sm: 'inherit' } }}
            >
              Analytics
            </Button>
          </Box>
        </Box>


        {/* Budget Overview */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Budget Pots Section - Left Column */}
          <Grid item xs={12} md={6}>
            <Card sx={styles.card.default}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={styles.layout.flexRow} gap={1}>
                    <AccountBalanceIcon sx={{ color: '#4CAF50' }} />
                    <Typography variant="h6">Budget Pots</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingPot(null);
                      setPotForm({ name: '', amount: '', type: 'couple', contributorName: '' });
                      setShowPotDialog(true);
                    }}
                  >
                    Add Pot
                  </Button>
                </Box>
                
                {budgetData.pots.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No budget pots yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Create budget pots to track contributions from different sources
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <List dense>
                      {budgetData.pots.map((pot) => (
                        <ListItem
                          key={pot.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            pr: 8, // Add padding to prevent overlap with secondaryAction
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => {
                                  setEditingPot(pot);
                                  setPotForm({
                                    name: pot.name,
                                    amount: pot.amount.toString(),
                                    type: pot.type,
                                    contributorName: pot.contributorName || ''
                                  });
                                  setShowPotDialog(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight="medium">
                                {pot.name}
                                {pot.type === 'other' && pot.contributorName && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({pot.contributorName})
                                  </Typography>
                                )}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                                {CURRENCY_SYMBOL}{pot.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Pots
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {CURRENCY_SYMBOL}{totalBudgetFromPots.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Total Budget & Spent Card - Right Column */}
          <Grid item xs={12} md={6}>
            <Card sx={styles.card.default}>
              <CardContent>
                <Box sx={{ ...styles.layout.flexRow, mb: 3 }} gap={1}>
                  <Typography variant="h6">Total Budget</Typography>
                </Box>
                <Typography variant="h4" color="primary" sx={{ mb: 3 }}>
                  {CURRENCY_SYMBOL}{totalBudgetFromPots.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ ...styles.layout.flexRow, mb: 2 }} gap={1}>
                  <Typography variant="h6">Total Spent</Typography>
                </Box>
                <Typography variant="h4" color="primary" sx={{ mb: 3 }}>
                  {CURRENCY_SYMBOL}{totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ ...styles.layout.flexRow, mb: 2 }} gap={1}>
                  <Typography variant="h6">Remaining Budget</Typography>
                </Box>
                <Typography variant="h4" color={remainingBudget >= 0 ? 'success.main' : 'error.main'}>
                  {CURRENCY_SYMBOL}{remainingBudget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {remainingBudget < 0 && (
                  <Chip 
                    icon={<WarningIcon />} 
                    label="Over Budget" 
                    color="error" 
                    size="small" 
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for Pots, Categories, Items, Templates */}
        <Card sx={{ ...styles.card.default, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, pt: 1, flexWrap: 'wrap' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Budget Items" />
              <Tab label="Budget Pots" />
              <Tab label="Categories" />
              <Tab label="Templates" />
            </Tabs>
            <Button
              variant="contained"
              startIcon={<ChartPie size={20} />}
              onClick={() => setShowRecommendationDialog(true)}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                '&:hover': { bgcolor: 'primary.dark' },
                '& .MuiButton-startIcon': { color: 'common.white' },
              }}
            >
              Get Recommendations
            </Button>
          </Box>

          {/* Budget Items Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* Filters and Sort */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                  >
                    Filters
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortConfig.field}
                      label="Sort By"
                      onChange={(e) => setSortConfig(prev => ({ ...prev, field: e.target.value }))}
                    >
                      <MenuItem value="order">Default Order</MenuItem>
                      <MenuItem value="category">Category</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="pot">Pot Source</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<SortIcon />}
                    onClick={() => setSortConfig(prev => ({ 
                      ...prev, 
                      direction: prev.direction === 'asc' ? 'desc' : 'asc' 
                    }))}
                    size="small"
                  >
                    {sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setShowItemForm(true);
                      setEditingItem(null);
                      setNewBudgetItem({
                        category: '',
                        description: '',
                        estimatedCost: '',
                        actualCost: '',
                        amount: '',
                        paid: false,
                        paidDate: null,
                        paymentMethod: '',
                        potSourceId: '',
                        vendorId: '',
                        notes: '',
                        order: 0
                      });
                    }}
                  >
                    Add Item
                  </Button>
                </Box>
              </Box>

              {/* Filter Panel */}
              <Collapse in={showFilters}>
                <Card sx={{ mb: 2, p: 2, bgcolor: 'background.default' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search"
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        placeholder="Search items..."
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink={true}>Category</InputLabel>
                        <Select
                          value={filters.category || ''}
                          label="Category"
                          onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected === '') {
                              return 'All Categories';
                            }
                            return selected;
                          }}
                        >
                          <MenuItem value="">All Categories</MenuItem>
                          {availableCategories.map(cat => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink={true}>Pot Source</InputLabel>
                        <Select
                          value={filters.pot || ''}
                          label="Pot Source"
                          onChange={(e) => setFilters(prev => ({ ...prev, pot: e.target.value }))}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected === '') {
                              return 'All Pots';
                            }
                            const selectedPot = budgetData.pots.find(p => p.id === selected);
                            return selectedPot ? selectedPot.name : selected;
                          }}
                        >
                          <MenuItem value="">All Pots</MenuItem>
                          {budgetData.pots.map(pot => (
                            <MenuItem key={pot.id} value={pot.id}>{pot.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink={true}>Vendor</InputLabel>
                        <Select
                          value={filters.vendor || ''}
                          label="Vendor"
                          onChange={(e) => setFilters(prev => ({ ...prev, vendor: e.target.value }))}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected === '') {
                              return 'All Vendors';
                            }
                            const selectedVendor = vendors.find(v => v && v._id === selected);
                            return selectedVendor ? selectedVendor.name : selected;
                          }}
                        >
                          <MenuItem value="">All Vendors</MenuItem>
                          {vendors.filter(v => v && v._id).map(vendor => (
                            <MenuItem key={vendor._id} value={vendor._id}>{vendor.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                          value={filters.paidStatus}
                          label="Payment Status"
                          onChange={(e) => setFilters(prev => ({ ...prev, paidStatus: e.target.value }))}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="paid">Paid</MenuItem>
                          <MenuItem value="unpaid">Unpaid</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Amount"
                        type="number"
                        value={filters.minAmount}
                        onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Amount"
                        type="number"
                        value={filters.maxAmount}
                        onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Date From"
                        value={filters.dateFrom}
                        onChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Date To"
                        value={filters.dateTo}
                        onChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Collapse>

              {/* Bulk Actions */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.selected', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {selectedItems.length} item(s) selected
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedItems([])}
                    >
                      Clear Selection
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleBulkDelete}
                      disabled={saving}
                    >
                      Delete Selected ({selectedItems.length})
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Budget Items Table */}
              <DragDropContext onDragEnd={handleDragEnd}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" width="50">
                          <Checkbox
                            indeterminate={selectedItems.length > 0 && selectedItems.length < filteredItems.length}
                            checked={filteredItems.length > 0 && selectedItems.length === filteredItems.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </TableCell>
                        <TableCell width="40"></TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Estimated</TableCell>
                        <TableCell>Actual</TableCell>
                        <TableCell>Variance</TableCell>
                        <TableCell>Pot Source</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Paid</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <Droppable droppableId="budget-items">
                      {(provided, snapshot) => (
                        <TableBody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent'
                          }}
                        >
                          {filteredItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                                <Box sx={styles.patterns.emptyState}>
                                  <CurrencyGbp size={60} weight="regular" style={{ color: 'text.secondary', mb: 2 }} />
                                  <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No budget items yet
                                  </Typography>
                                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                                    Start tracking your wedding expenses
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowItemForm(true)}
                                  >
                                    Add First Item
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredItems.map((item, index) => {
                              const estimated = parseFloat(item.estimatedCost || item.amount || 0);
                              const actual = parseFloat(item.actualCost || 0);
                              const variance = actual > 0 ? actual - estimated : 0;
                              const variancePercent = estimated > 0 ? ((variance / estimated) * 100) : 0;
                              // Handle both old and new formats for display
                              let potSourcesArray = [];
                              if (item.potSources && Array.isArray(item.potSources) && item.potSources.length > 0) {
                                // New format: get all pots with amounts
                                potSourcesArray = item.potSources.map(ps => {
                                  const pot = budgetData.pots.find(p => p.id === ps.potId);
                                  return pot ? { pot, amount: ps.amount } : null;
                                }).filter(Boolean);
                              } else if (item.potSourceId) {
                                // Old format: show single pot
                                const pot = budgetData.pots.find(p => p.id === item.potSourceId);
                                if (pot) {
                                  potSourcesArray = [{ pot, amount: null }];
                                }
                              }
                              const vendor = item.vendorId ? vendors.find(v => v._id === item.vendorId) : null;
                              const canDrag = sortConfig.field === 'order';

                              return (
                                <Draggable 
                                  key={item.id} 
                                  draggableId={item.id} 
                                  index={index}
                                  isDragDisabled={!canDrag}
                                >
                                  {(provided, snapshot) => (
                                    <TableRow
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      sx={{
                                        bgcolor: snapshot.isDragging ? 'action.selected' : (selectedItems.includes(item.id) ? 'action.selected' : 'transparent'),
                                        '&:hover': {
                                          bgcolor: 'action.hover'
                                        }
                                      }}
                                    >
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          checked={selectedItems.includes(item.id)}
                                          onChange={() => handleToggleItem(item.id)}
                                        />
                                      </TableCell>
                                      <TableCell 
                                        {...(canDrag ? provided.dragHandleProps : {})}
                                        sx={{ 
                                          cursor: canDrag ? 'grab' : 'default',
                                          opacity: canDrag ? 1 : 0.3,
                                          userSelect: 'none',
                                          width: '50px'
                                        }}
                                      >
                                        {canDrag && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'grab',
                                              '&:active': {
                                                cursor: 'grabbing'
                                              }
                                            }}
                                          >
                                            <DragIndicatorIcon fontSize="small" color="action" />
                                          </Box>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {(() => {
                                          // Case-insensitive category matching
                                          const category = budgetData.categories.find(c => 
                                            c.name?.toLowerCase() === item.category?.toLowerCase()
                                          );
                                          const categoryColor = category?.color;
                                          const isLight = categoryColor ? isLightColor(categoryColor) : false;
                                          
                                          return (
                                            <Chip 
                                              label={item.category || 'Uncategorized'} 
                                              size="small" 
                                              color="primary" 
                                              variant="outlined"
                                              sx={{ 
                                                bgcolor: categoryColor || 'transparent',
                                                color: categoryColor 
                                                  ? (isLight ? '#1a1a1a' : '#fff')
                                                  : undefined,
                                                fontWeight: categoryColor && isLight ? 700 : 'normal',
                                                border: categoryColor 
                                                  ? (isLight ? '1.5px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)')
                                                  : undefined,
                                                position: 'relative',
                                                '&::before': categoryColor && isLight ? {
                                                  content: '""',
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  right: 0,
                                                  bottom: 0,
                                                  background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.05))',
                                                  borderRadius: 'inherit',
                                                  pointerEvents: 'none',
                                                } : {},
                                                '& .MuiChip-label': {
                                                  position: 'relative',
                                                  zIndex: 1,
                                                  textShadow: categoryColor && isLight
                                                    ? '0 0 3px rgba(255, 255, 255, 0.9), 0 0 6px rgba(255, 255, 255, 0.6), 0 1px 2px rgba(255, 255, 255, 0.8)'
                                                    : categoryColor && !isLight
                                                    ? '0 1px 2px rgba(0, 0, 0, 0.6)'
                                                    : 'none',
                                                  filter: categoryColor && isLight ? 'contrast(1.2)' : 'none',
                                                }
                                              }}
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                      <TableCell>{item.description}</TableCell>
                                      <TableCell>{CURRENCY_SYMBOL}{estimated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                      <TableCell>
                                        {actual > 0 ? (
                                          CURRENCY_SYMBOL + actual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {actual > 0 && (
                                          <Chip
                                            size="small"
                                            label={`${variancePercent > 0 ? '+' : ''}${variancePercent.toFixed(1)}%`}
                                            color={Math.abs(variancePercent) > 10 ? 'error' : 'default'}
                                            variant="outlined"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {potSourcesArray.length > 0 ? (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {potSourcesArray.map((ps, idx) => {
                                              const label = ps.amount !== null && ps.amount !== undefined
                                                ? `${ps.pot.name} (${CURRENCY_SYMBOL}${parseFloat(ps.amount || 0).toFixed(2)})`
                                                : ps.pot.name;
                                              return (
                                                <Chip
                                                  key={idx}
                                                  label={label}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ maxWidth: 200 }}
                                                />
                                              );
                                            })}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {vendor ? (
                                          <Tooltip title={`${vendor.name} - ${vendor.category || 'N/A'}`}>
                                            <Chip 
                                              label={vendor.name} 
                                              size="small" 
                                              variant="outlined"
                                              color="primary"
                                              sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            />
                                          </Tooltip>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {item.paid ? (
                                          <Chip icon={<CheckCircleIcon />} label="Paid" color="success" size="small" />
                                        ) : (
                                          <Chip label="Unpaid" size="small" variant="outlined" />
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEditItem(item)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleRemoveBudgetItem(item.id)}>
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </Table>
                </TableContainer>
              </DragDropContext>
              {sortConfig.field !== 'order' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Drag-and-drop reordering is only available when sorted by "Default Order". Change the sort option to enable drag-and-drop.
                </Alert>
              )}
            </Box>
          )}

          {/* Budget Pots Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Budget Pots</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingPot(null);
                    setPotForm({ name: '', amount: '', type: 'couple', contributorName: '' });
                    setShowPotDialog(true);
                  }}
                >
                  Add Pot
                </Button>
              </Box>

              <Grid container spacing={2}>
                {budgetData.pots.map((pot) => {
                  // Find items using this pot (support both old and new formats)
                  const potItems = budgetData.items.filter(item => {
                    // Check old format (potSourceId)
                    if (item.potSourceId === pot.id) return true;
                    // Check new format (potSources array)
                    if (item.potSources && Array.isArray(item.potSources)) {
                      return item.potSources.some(ps => ps.potId === pot.id);
                    }
                    return false;
                  });
                  // Calculate spent amount - for new format, use the amount from potSources
                  const potSpent = potItems.reduce((sum, item) => {
                    if (item.potSources && Array.isArray(item.potSources)) {
                      // New format: sum the amounts from potSources for this pot
                      const potSource = item.potSources.find(ps => ps.potId === pot.id);
                      if (potSource && potSource.amount) {
                        return sum + parseFloat(potSource.amount);
                      }
                    } else {
                      // Old format: use item cost
                      const cost = item.actualCost || item.estimatedCost || item.amount || 0;
                      return sum + parseFloat(cost);
                    }
                    return sum;
                  }, 0);
                  const potRemaining = pot.amount - potSpent;
                  const potPercentage = pot.amount > 0 ? (potSpent / pot.amount) * 100 : 0;

                  return (
                    <Grid item xs={12} md={6} lg={4} key={pot.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6">{pot.name}</Typography>
                              {pot.type === 'other' && pot.contributorName && (
                                <Typography variant="body2" color="text.secondary">
                                  {pot.contributorName}
                                </Typography>
                              )}
                            </Box>
                            <Box>
                              <IconButton size="small" onClick={() => {
                                setEditingPot(pot);
                                setPotForm({
                                  name: pot.name,
                                  amount: pot.amount.toString(),
                                  type: pot.type,
                                  contributorName: pot.contributorName || ''
                                });
                                setShowPotDialog(true);
                              }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Typography variant="h5" color="primary">
                            {CURRENCY_SYMBOL}{pot.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>

                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">Spent: {CURRENCY_SYMBOL}{potSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                              <Typography variant="body2">{potPercentage.toFixed(1)}%</Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(potPercentage, 100)} 
                              color={potPercentage > 100 ? 'error' : potPercentage > 80 ? 'warning' : 'primary'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" color={potRemaining >= 0 ? 'text.secondary' : 'error.main'} sx={{ mt: 1 }}>
                              Remaining: {CURRENCY_SYMBOL}{potRemaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {potItems.length} item(s)
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {budgetData.pots.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AccountBalanceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No budget pots yet
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Create budget pots to track contributions from different sources
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowPotDialog(true)}
                  >
                    Add First Pot
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Categories Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Categories</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingCategory(null);
                    setCategoryForm({ name: '', color: '#85929E', order: 0, goalAmount: '' });
                    setShowCategoryDialog(true);
                  }}
                >
                  Add Category
                </Button>
              </Box>

              <Grid container spacing={2}>
                {budgetData.categories.map((category) => {
                  const categoryItems = budgetData.items.filter(item => item.category === category.name);
                  const categorySpent = categoryItems.reduce((sum, item) => {
                    const cost = item.actualCost || item.estimatedCost || item.amount || 0;
                    return sum + parseFloat(cost);
                  }, 0);
                  const goalProgress = category.goalAmount ? (categorySpent / category.goalAmount) * 100 : null;

                  return (
                    <Grid item xs={12} md={6} lg={4} key={category.name}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 1,
                                  bgcolor: category.color,
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}
                              />
                              <Box>
                                <Typography variant="h6">{category.name}</Typography>
                                {category.isDefault && (
                                  <Chip label="Default" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                                )}
                              </Box>
                            </Box>
                            {!category.isDefault && (
                              <Box>
                                <IconButton size="small" onClick={() => {
                                  setEditingCategory(category);
                                  setCategoryForm({
                                    name: category.name,
                                    color: category.color || '#85929E',
                                    order: category.order || 0,
                                    goalAmount: category.goalAmount ? category.goalAmount.toString() : ''
                                  });
                                  setShowCategoryDialog(true);
                                }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteCategory(category.name)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>

                          <Typography variant="h5" color="primary">
                            {CURRENCY_SYMBOL}{categorySpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>

                          {category.goalAmount && (
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Goal: {CURRENCY_SYMBOL}{category.goalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                <Typography variant="body2">{goalProgress ? goalProgress.toFixed(1) + '%' : '-'}</Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(goalProgress || 0, 100)} 
                                color={goalProgress > 100 ? 'error' : goalProgress > 80 ? 'warning' : 'primary'}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                          )}

                          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            {categoryItems.length} item(s)
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {budgetData.categories.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CategoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No categories yet
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Default categories will be created automatically when you add your first item
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCategoryDialog(true)}
                  >
                    Add Custom Category
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Templates Tab */}
          {activeTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Budget Templates</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setTemplateForm({ name: '', description: '', weddingType: 'custom' });
                    setShowSaveTemplateDialog(true);
                  }}
                >
                  Save Current as Template
                </Button>
              </Box>

              {loadingTemplates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* System Templates */}
                  {templates.systemTemplates.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>System Templates</Typography>
                      <Grid container spacing={2}>
                        {templates.systemTemplates.map((template) => (
                          <Grid item xs={12} md={6} lg={4} key={template._id}>
                            <Card>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6">{template.name}</Typography>
                                    {template.description && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {template.description}
                                      </Typography>
                                    )}
                                    {template.weddingType && template.weddingType !== 'custom' && (
                                      <Chip 
                                        label={template.weddingType} 
                                        size="small" 
                                        sx={{ mt: 1 }} 
                                      />
                                    )}
                                  </Box>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {template.categories?.length || 0} categories
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {template.items?.length || 0} items
                                  </Typography>
                                  {template.usageCount > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                      Used {template.usageCount} time(s)
                                    </Typography>
                                  )}
                                </Box>

                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => {
                                    setShowTemplateDialog(true);
                                    setEditingTemplate(template);
                                  }}
                                >
                                  Apply Template
                                </Button>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Custom Templates */}
                  {templates.customTemplates.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>Your Templates</Typography>
                      <Grid container spacing={2}>
                        {templates.customTemplates.map((template) => (
                          <Grid item xs={12} md={6} lg={4} key={template._id}>
                            <Card>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6">{template.name}</Typography>
                                    {template.description && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {template.description}
                                      </Typography>
                                    )}
                                    {template.weddingType && template.weddingType !== 'custom' && (
                                      <Chip 
                                        label={template.weddingType} 
                                        size="small" 
                                        sx={{ mt: 1 }} 
                                      />
                                    )}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteTemplate(template._id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {template.categories?.length || 0} categories
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {template.items?.length || 0} items
                                  </Typography>
                                  {template.usageCount > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                      Used {template.usageCount} time(s)
                                    </Typography>
                                  )}
                                </Box>

                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => {
                                    setShowTemplateDialog(true);
                                    setEditingTemplate(template);
                                  }}
                                >
                                  Apply Template
                                </Button>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {templates.systemTemplates.length === 0 && templates.customTemplates.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No templates available
                      </Typography>
                      <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Save your current budget as a template to reuse it later
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setTemplateForm({ name: '', description: '', weddingType: 'custom' });
                          setShowSaveTemplateDialog(true);
                        }}
                      >
                        Save Current as Template
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Card>

        {/* Add/Edit Budget Item Dialog */}
        <Dialog 
          open={showItemForm} 
          onClose={() => {
            setShowItemForm(false);
            setEditingItem(null);
            setNewBudgetItem({
              category: '',
              description: '',
              estimatedCost: '',
              actualCost: '',
              amount: '',
              paid: false,
              paidDate: null,
              paymentDueDate: null,
              paymentMethod: '',
              milestoneId: '',
              potSourceId: '',
              potSources: [],
              vendorId: '',
              notes: '',
              order: 0
            });
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingItem ? 'Edit Budget Item' : 'Add Budget Item'}
          </DialogTitle>
          <DialogContent dividers>

            <Grid container spacing={2} sx={{ mt: 1 }}>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Item Name *"
                  value={newBudgetItem.description}
                  onChange={handleBudgetItemChange('description')}
                  placeholder="e.g., Venue rental for ceremony"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={newBudgetItem.category}
                    label="Category *"
                    onChange={handleBudgetItemChange('category')}
                  >
                    {availableCategories.map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {categoryRecommendation && categoryRecommendation !== newBudgetItem.category && (
                  <Alert 
                    severity="info" 
                    sx={{ mt: 1 }}
                    action={
                      <Button size="small" onClick={applyRecommendedCategory}>
                        Apply
                      </Button>
                    }
                  >
                    AI suggests: {categoryRecommendation}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Cost *"
                  type="number"
                  value={newBudgetItem.estimatedCost}
                  onChange={handleBudgetItemChange('estimatedCost')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Actual Cost"
                  type="number"
                  value={newBudgetItem.actualCost}
                  onChange={handleBudgetItemChange('actualCost')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>
                  }}
                  helperText="Leave blank if not yet known"
                />
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Pot Sources (Optional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Select one or more budget pots and specify the amount each pot contributes to this item
                  </Typography>
                  
                  {newBudgetItem.potSources && newBudgetItem.potSources.map((potSource, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <FormControl fullWidth>
                            <InputLabel>Pot</InputLabel>
                            <Select
                              value={potSource.potId || ''}
                              label="Pot"
                              onChange={(e) => {
                                const updatedPotSources = [...newBudgetItem.potSources];
                                updatedPotSources[index] = { ...updatedPotSources[index], potId: e.target.value };
                                setNewBudgetItem(prev => ({ ...prev, potSources: updatedPotSources }));
                              }}
                            >
                              <MenuItem value="">Select a pot</MenuItem>
                              {budgetData.pots
                                .filter(pot => !newBudgetItem.potSources.some((ps, i) => i !== index && ps.potId === pot.id))
                                .map(pot => (
                                  <MenuItem key={pot.id} value={pot.id}>{pot.name}</MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Amount"
                            type="number"
                            value={potSource.amount || ''}
                            onChange={(e) => {
                              const updatedPotSources = [...newBudgetItem.potSources];
                              updatedPotSources[index] = { ...updatedPotSources[index], amount: e.target.value };
                              setNewBudgetItem(prev => ({ ...prev, potSources: updatedPotSources }));
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>
                            }}
                            placeholder="0.00"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <IconButton
                            onClick={() => {
                              const updatedPotSources = newBudgetItem.potSources.filter((_, i) => i !== index);
                              setNewBudgetItem(prev => ({ ...prev, potSources: updatedPotSources }));
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setNewBudgetItem(prev => ({
                        ...prev,
                        potSources: [...(prev.potSources || []), { potId: '', amount: '' }]
                      }));
                    }}
                    disabled={budgetData.pots.length === 0 || (newBudgetItem.potSources && newBudgetItem.potSources.length >= budgetData.pots.length)}
                    size="small"
                  >
                    Add Pot Source
                  </Button>
                  
                  {newBudgetItem.potSources && newBudgetItem.potSources.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Total from pots: {CURRENCY_SYMBOL}
                        {newBudgetItem.potSources
                          .reduce((sum, ps) => sum + (parseFloat(ps.amount) || 0), 0)
                          .toFixed(2)}
                        {newBudgetItem.estimatedCost && (
                          <span>
                            {' '}(Item total: {CURRENCY_SYMBOL}{parseFloat(newBudgetItem.estimatedCost).toFixed(2)})
                          </span>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Vendor (Optional)</InputLabel>
                  <Select
                    value={newBudgetItem.vendorId || ''}
                    label="Vendor (Optional)"
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      setNewBudgetItem(prev => ({ ...prev, vendorId: vendorId || '' }));
                      
                      // Auto-populate estimated cost if vendor is selected and has booking info
                      // This would need booking integration - placeholder for now
                      if (vendorId) {
                        const vendor = vendors.find(v => v && v._id === vendorId);
                        if (vendor) {
                          // Could fetch booking info here to get cost
                        }
                      }
                    }}
                    disabled={loadingVendors}
                  >
                    <MenuItem value="">None</MenuItem>
                    {vendors.filter(v => v && v._id).map(vendor => (
                      <MenuItem key={vendor._id} value={vendor._id}>
                        {vendor.name} {vendor.category ? `(${vendor.category})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingVendors && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Loading vendors...
                    </Typography>
                  )}
                  {vendors.length === 0 && !loadingVendors && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      No vendors linked to this wedding. Add vendors from the Suppliers section.
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Milestone (Optional)</InputLabel>
                  <Select
                    value={newBudgetItem.milestoneId || ''}
                    label="Milestone (Optional)"
                    onChange={handleBudgetItemChange('milestoneId')}
                    disabled={loadingMilestones}
                  >
                    <MenuItem value="">None</MenuItem>
                    {milestones.filter(m => m && m.id).map(milestone => (
                      <MenuItem key={milestone.id} value={milestone.id}>
                        {milestone.name} {milestone.date ? `(${format(new Date(milestone.date), 'MMM d, yyyy')})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingMilestones && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Loading milestones...
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Payment Due Date (Optional)"
                  value={newBudgetItem.paymentDueDate}
                  onChange={(date) => setNewBudgetItem(prev => ({ ...prev, paymentDueDate: date }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newBudgetItem.paid}
                      onChange={handleBudgetItemChange('paid')}
                    />
                  }
                  label="Mark as Paid"
                />
              </Grid>

              {newBudgetItem.paid && (
                <>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Payment Date"
                      value={newBudgetItem.paidDate}
                      onChange={(date) => setNewBudgetItem(prev => ({ ...prev, paidDate: date }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={newBudgetItem.paymentMethod || ''}
                        label="Payment Method"
                        onChange={handleBudgetItemChange('paymentMethod')}
                      >
                        <MenuItem value="">Not specified</MenuItem>
                        {PAYMENT_METHODS.map(method => (
                          <MenuItem key={method} value={method}>{method}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={newBudgetItem.notes}
                  onChange={handleBudgetItemChange('notes')}
                  placeholder="Additional notes about this item..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowItemForm(false);
              setEditingItem(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddBudgetItem}
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : (editingItem ? 'Update' : 'Add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Pot Dialog */}
        <Dialog 
          open={showPotDialog} 
          onClose={() => {
            setShowPotDialog(false);
            setEditingPot(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingPot ? 'Edit Budget Pot' : 'Add Budget Pot'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pot Name *"
                  value={potForm.name}
                  onChange={(e) => setPotForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Couple, Other contributor"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Pot Type *</InputLabel>
                  <Select
                    value={potForm.type}
                    label="Pot Type *"
                    onChange={(e) => setPotForm(prev => ({ ...prev, type: e.target.value }))}
                  >
                    {POT_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {potForm.type === 'other' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contributor Name"
                    value={potForm.contributorName}
                    onChange={(e) => setPotForm(prev => ({ ...prev, contributorName: e.target.value }))}
                    placeholder="Name of contributor"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount *"
                  type="number"
                  value={potForm.amount}
                  onChange={(e) => setPotForm(prev => ({ ...prev, amount: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {editingPot && (
              <Button 
                onClick={() => handleDeletePot(editingPot.id)}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={saving}
                sx={{ mr: 'auto' }}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => {
              setShowPotDialog(false);
              setEditingPot(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddPot}
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Category Dialog */}
        <Dialog 
          open={showCategoryDialog} 
          onClose={() => {
            setShowCategoryDialog(false);
            setEditingCategory(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingCategory ? 'Edit Category' : 'Add Custom Category'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Name *"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Videography, Stationery"
                  disabled={editingCategory?.isDefault}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Colour"
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Order"
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Goal Amount (Optional)"
                  type="number"
                  value={categoryForm.goalAmount}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, goalAmount: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>
                  }}
                  helperText="Set a target budget for this category"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowCategoryDialog(false);
              setEditingCategory(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCategory}
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Budget Recommendations Dialog */}
        <Dialog 
          open={showRecommendationDialog} 
          onClose={() => {
            setShowRecommendationDialog(false);
            setBudgetRecommendations(null);
            setAppliedRecommendations(new Set());
            setGuestCount('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChartPie size={24} weight="regular" style={{ marginRight: 12, color: '#3f51b5' }} />
              Smart Budget Recommendations
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {!budgetRecommendations ? (
              <>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Get industry-average budget allocation recommendations based on your total budget.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2, mt: 3 }}>
                  Total Budget: {CURRENCY_SYMBOL}{totalBudgetFromPots.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                
                <TextField
                  fullWidth
                  label="Guest Count (Optional)"
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value === '' ? '' : e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Number of guests attending your wedding"
                />
                
                {totalBudgetFromPots === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You need to add budget pots first to get recommendations. The total budget is calculated from the sum of all your budget pots.
                  </Alert>
                )}
                
                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.dark">
                    <LightbulbIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Recommendations are based on industry averages and will be calculated based on your total budget from all pots.
                  </Typography>
                </Box>
                
                {loadingRecommendations && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="body1" paragraph>
                  Based on your wedding details, here's our recommended budget allocation:
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  You can apply individual recommendations using the "Apply" button next to each item. Once you've applied individual recommendations, the "Apply All" button will be disabled to prevent duplicates.
                </Alert>
                
                {budgetRecommendations.note && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {budgetRecommendations.note}
                  </Alert>
                )}
                
                <List>
                  {Object.entries(budgetRecommendations.recommendations || budgetRecommendations).map(([category, data]) => {
                    const percentage = typeof data === 'object' && data.percentage ? data.percentage : data;
                    const amount = typeof data === 'object' && data.amount ? data.amount : (totalBudgetFromPots * (percentage / 100));
                    const range = typeof data === 'object' && data.range ? data.range : null;
                    const isApplied = appliedRecommendations.has(category);

                    return (
                      <ListItem 
                        key={category} 
                        divider
                        secondaryAction={
                          <Button
                            variant={isApplied ? "outlined" : "contained"}
                            size="small"
                            onClick={() => applySingleRecommendation(category, data)}
                            disabled={isApplied || saving}
                            startIcon={isApplied ? <CheckCircleIcon /> : null}
                          >
                            {isApplied ? 'Applied' : 'Apply'}
                          </Button>
                        }
                      >
                        <ListItemText 
                          primary={`${category.charAt(0).toUpperCase() + category.slice(1)}`} 
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                {percentage}% • {CURRENCY_SYMBOL}{typeof amount === 'number' ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : amount}
                              </Typography>
                              {range && (
                                <Typography variant="caption" color="text.secondary">
                                  Typical range: {CURRENCY_SYMBOL}{range.min.toLocaleString()} - {CURRENCY_SYMBOL}{range.max.toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {!budgetRecommendations ? (
              <>
                <Button onClick={() => setShowRecommendationDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleGetRecommendations}
                  variant="contained" 
                  disabled={loadingRecommendations || totalBudgetFromPots === 0}
                >
                  Get Recommendations
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => {
                    setShowRecommendationDialog(false);
                    setBudgetRecommendations(null);
                    setAppliedRecommendations(new Set());
                    setGuestCount('');
                  }}
                  disabled={saving}
                >
                  Close
                </Button>
                <Tooltip 
                  title={
                    appliedRecommendations.size > 0 
                      ? "Cannot apply all recommendations after applying individual ones. Close and reopen to reset." 
                      : budgetData.items && budgetData.items.length > 0
                      ? "Cannot apply all recommendations when budget items already exist. Please remove existing items first."
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button 
                      onClick={applyRecommendations}
                      variant="contained" 
                      color="primary"
                      disabled={saving || appliedRecommendations.size > 0 || (budgetData.items && budgetData.items.length > 0)}
                      startIcon={saving ? <CircularProgress size={20} /> : null}
                    >
                      {saving ? 'Applying All...' : 'Apply All Recommendations'}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Spending Analysis Dialog */}
        <Dialog 
          open={showAnalysisDialog} 
          onClose={() => setShowAnalysisDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LightbulbIcon color="primary" sx={{ mr: 1 }} />
              Budget Analytics
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {spendingAnalysis && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Budget Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                      <Typography variant="h6">{CURRENCY_SYMBOL}{spendingAnalysis.totalBudget?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Total Spent</Typography>
                      <Typography variant="h6">{CURRENCY_SYMBOL}{spendingAnalysis.totalSpent?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Remaining</Typography>
                      <Typography 
                        variant="h6" 
                        color={spendingAnalysis.remainingBudget >= 0 ? 'success.main' : 'error.main'}
                      >
                        {CURRENCY_SYMBOL}{spendingAnalysis.remainingBudget?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" gutterBottom>Category Analysis</Typography>
                
                {spendingAnalysis.categoryAnalysis && spendingAnalysis.categoryAnalysis.length > 0 ? (
                  spendingAnalysis.categoryAnalysis.map((category) => (
                    <Box key={category.category} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1">
                          {category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {CURRENCY_SYMBOL}{category.actualCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Chip 
                            size="small"
                            label={`${typeof category.percentage === 'number' ? category.percentage.toFixed(2) : category.percentage}%`}
                            color={category.status === 'over' ? 'error' : category.status === 'under' ? 'info' : 'success'}
                          />
                        </Box>
                      </Box>
                      
                      {category.goalAmount && (
                        <>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((category.actualCost / category.goalAmount) * 100, 100)}
                            color={category.actualCost > category.goalAmount ? 'error' : category.actualCost > category.goalAmount * 0.8 ? 'warning' : 'success'}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Goal: {CURRENCY_SYMBOL}{category.goalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" color={category.status === 'over' ? 'error.main' : 'text.secondary'}>
                              {category.isOverBudget ? 'Over Budget' : 'On Track'}
                            </Typography>
                          </Box>
                        </>
                      )}

                      {category.variance !== undefined && category.variance !== 0 && (
                        <Typography variant="caption" color={category.variance > 0 ? 'error.main' : 'success.main'} sx={{ mt: 0.5, display: 'block' }}>
                          Variance: {category.variance > 0 ? '+' : ''}{CURRENCY_SYMBOL}{category.variance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({category.variancePercentage > 0 ? '+' : ''}{typeof category.variancePercentage === 'number' ? category.variancePercentage.toFixed(2) : category.variancePercentage}%)
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary">No category analysis available yet.</Typography>
                )}
                
                {spendingAnalysis.potBreakdown && spendingAnalysis.potBreakdown.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Pot Breakdown</Typography>
                    {spendingAnalysis.potBreakdown.map((pot) => (
                      <Box key={pot.potId} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1">{pot.potName}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {CURRENCY_SYMBOL}{pot.potSpent?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {CURRENCY_SYMBOL}{pot.potAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            <Chip 
                              size="small"
                              label={`${pot.percentage ? (typeof pot.percentage === 'number' ? pot.percentage.toFixed(2) : pot.percentage) : '0.00'}%`}
                              color={pot.isOverBudget ? 'error' : 'default'}
                            />
                          </Box>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(pot.percentage || 0, 100)} 
                          color={pot.isOverBudget ? 'error' : pot.percentage > 80 ? 'warning' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))}
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAnalysisDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Apply Template Dialog */}
        <Dialog
          open={showTemplateDialog}
          onClose={() => {
            setShowTemplateDialog(false);
            setEditingTemplate(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Apply Template</DialogTitle>
          <DialogContent>
            {editingTemplate && (
              <>
                <Typography variant="h6" gutterBottom>{editingTemplate.name}</Typography>
                {editingTemplate.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {editingTemplate.description}
                  </Typography>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Categories:</strong> {editingTemplate.categories?.length || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Items:</strong> {editingTemplate.items?.length || 0}
                  </Typography>
                  {editingTemplate.pots && editingTemplate.pots.length > 0 && (
                    <Typography variant="body2">
                      <strong>Pots:</strong> {editingTemplate.pots.length}
                    </Typography>
                  )}
                </Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Applying this template will add its categories, pots, and items to your current budget. 
                  You can choose to replace your existing budget or merge with it.
                </Alert>
                <FormControlLabel
                  control={
                    <Switch
                      checked={false}
                      onChange={(_e) => {
                        // This will be handled in the apply button
                      }}
                      disabled
                    />
                  }
                  label="Replace existing budget (merge if unchecked)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Note: You can choose replace or merge when clicking Apply below.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowTemplateDialog(false);
              setEditingTemplate(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => editingTemplate && handleApplyTemplate(editingTemplate._id, false)}
              variant="outlined"
              disabled={applyingTemplate || !editingTemplate}
            >
              {applyingTemplate ? <CircularProgress size={20} /> : 'Merge'}
            </Button>
            <Button
              onClick={() => editingTemplate && handleApplyTemplate(editingTemplate._id, true)}
              variant="contained"
              disabled={applyingTemplate || !editingTemplate}
            >
              {applyingTemplate ? <CircularProgress size={20} /> : 'Replace'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Template Dialog */}
        <Dialog
          open={showSaveTemplateDialog}
          onClose={() => {
            setShowSaveTemplateDialog(false);
            setTemplateForm({ name: '', description: '', weddingType: 'custom' });
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Save Current Budget as Template</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Template Name"
              value={templateForm.name}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mt: 2, mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={templateForm.description}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Wedding Type</InputLabel>
              <Select
                value={templateForm.weddingType}
                label="Wedding Type"
                onChange={(e) => setTemplateForm(prev => ({ ...prev, weddingType: e.target.value }))}
              >
                <MenuItem value="custom">Custom</MenuItem>
                {WEDDING_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will save your current budget structure (categories, pots, and items) as a reusable template.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowSaveTemplateDialog(false);
              setTemplateForm({ name: '', description: '', weddingType: 'custom' });
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              variant="contained"
              disabled={saving || !templateForm.name.trim()}
            >
              {saving ? <CircularProgress size={20} /> : 'Save Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography sx={{ pt: 2 }}>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
              }}
              color="error"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notifications */}
        <PortalSnackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
            {error}
          </Alert>
        </PortalSnackbar>

        <PortalSnackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
        >
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
            {success}
          </Alert>
        </PortalSnackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default WeddingBudget;
